import { useCallback, useEffect, useRef } from "react";

import {
  QUEUE_PHASE,
  isItemUploadReady,
  requireUploadFile,
} from "../propertyMediaQueue/propertyMediaQueue";

export const PROPERTY_MEDIA_UPLOAD_CONCURRENCY = 2;

function attemptKey(item) {
  const file = item.uploadFile || item.file;
  return [
    item.localId,
    file && file.name,
    file && file.size,
    file && file.lastModified,
    item.uploadGeneration || 0,
  ].join(":");
}

/**
 * Bounded auto-upload: one attempt per ready generation, concurrency 2.
 * Failed cards never auto-retry. Retry forgets that generation's attempt.
 */
export function usePropertyMediaUploadScheduler({
  propertyId,
  items,
  mediaQueue,
  uploadOne,
  onItemUploaded,
  enabled = true,
}) {
  const attemptedRef = useRef(new Set());
  const inflightRef = useRef(new Map());
  const itemsRef = useRef(items);
  const propertyIdRef = useRef(propertyId);
  const uploadOneRef = useRef(uploadOne);
  const onItemUploadedRef = useRef(onItemUploaded);
  const mediaQueueRef = useRef(mediaQueue);
  const enabledRef = useRef(enabled);
  const pumpingRef = useRef(false);

  itemsRef.current = items;
  propertyIdRef.current = propertyId;
  uploadOneRef.current = uploadOne;
  onItemUploadedRef.current = onItemUploaded;
  mediaQueueRef.current = mediaQueue;
  enabledRef.current = enabled;

  const forgetAttempts = useCallback((localId) => {
    Array.from(attemptedRef.current).forEach((key) => {
      if (String(key).startsWith(`${localId}:`)) {
        attemptedRef.current.delete(key);
      }
    });
  }, []);

  useEffect(() => {
    attemptedRef.current = new Set();
    inflightRef.current = new Map();
  }, [propertyId]);

  const findNextReady = useCallback(() => {
    if (!enabledRef.current) return null;
    const id = Number(propertyIdRef.current);
    if (!Number.isInteger(id) || id <= 0) return null;
    return (itemsRef.current || []).find((item) => {
      if (!isItemUploadReady(item)) return false;
      if (inflightRef.current.has(item.localId)) return false;
      return !attemptedRef.current.has(attemptKey(item));
    });
  }, []);

  const pump = useCallback(() => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;
    try {
      while (inflightRef.current.size < PROPERTY_MEDIA_UPLOAD_CONCURRENCY) {
        const next = findNextReady();
        if (!next) break;
        const key = attemptKey(next);
        attemptedRef.current.add(key);
        const startedPropertyId = propertyIdRef.current;
        const run = (async () => {
          mediaQueueRef.current.patchItem(next.localId, {
            phase: QUEUE_PHASE.uploading,
            uploadError: null,
          });
          try {
            const uploadFile = requireUploadFile(next);
            const result = await uploadOneRef.current(
              startedPropertyId,
              uploadFile,
              next.kind,
            );
            if (propertyIdRef.current !== startedPropertyId) return;
            const row = (result && result.images && result.images[0]) || null;
            if (row) {
              mediaQueueRef.current.removeItem(next.localId);
              try {
                await onItemUploadedRef.current?.(next, row, result);
              } catch (_err) {
                /* File already uploaded; caller owns post-upload recovery. */
              }
            } else {
              const errMsg = (result && result.errors && result.errors[0])
                || "Не вдалося завантажити файл.";
              mediaQueueRef.current.patchItem(next.localId, {
                phase: QUEUE_PHASE.upload_failed,
                uploadError: String(errMsg),
              });
            }
          } catch (err) {
            if (propertyIdRef.current !== startedPropertyId) return;
            mediaQueueRef.current.patchItem(next.localId, {
              phase: QUEUE_PHASE.upload_failed,
              uploadError: err && err.message ? err.message : "Не вдалося завантажити файл.",
            });
          } finally {
            inflightRef.current.delete(next.localId);
            pump();
          }
        })();
        inflightRef.current.set(next.localId, run);
      }
    } finally {
      pumpingRef.current = false;
    }
  }, [findNextReady]);

  useEffect(() => {
    if (!enabled) return undefined;
    pump();
    return undefined;
  }, [enabled, items, propertyId, pump]);

  const drain = useCallback(async (explicitPropertyId) => {
    if (explicitPropertyId != null) {
      propertyIdRef.current = explicitPropertyId;
    }
    pump();
    while (inflightRef.current.size > 0 || findNextReady()) {
      pump();
      const pending = Array.from(inflightRef.current.values());
      if (pending.length === 0) break;
      await Promise.allSettled(pending);
    }
  }, [findNextReady, pump]);

  const retryUpload = useCallback((localId) => {
    forgetAttempts(localId);
    mediaQueueRef.current.retryUpload(localId);
  }, [forgetAttempts]);

  const inflightCount = inflightRef.current.size;

  return {
    drain,
    retryUpload,
    inflightCount,
    concurrency: PROPERTY_MEDIA_UPLOAD_CONCURRENCY,
    forgetAttempts,
  };
}
